import { useEffect, useState, Suspense, lazy, useRef } from "react";
import { useContentProtection } from "./hooks/useContentProtection";
const CRTOverlay = lazy(() => import("./components/effects/CRTOverlay"));
const CookieConsent = lazy(() => import("./components/CookieConsent"));
import Navbar from "./components/Navbar";
import Hero from "./features/Hero";
// A /tv e' uma rota INTEIRA que so' e' montada quando `isTVRoute` e' true (ja'
// dentro de um Suspense, logo abaixo). Importada de forma estatica, ela e o que
// ela arrasta — inclusive o player e os efeitos — entravam no bundle da HOME e
// eram baixados e avaliados por quem nunca abre /tv.
const TVPage = lazy(() => import("./pages/TVPage"));

// Ultra-light DOM delivery using lazy dynamic imports for below-the-fold sections
const CanvasBackground = lazy(
  () => import("./components/effects/CanvasBackground"),
);
const Atmosphere = lazy(() => import("./features/Atmosphere"));
const AmericanBar = lazy(() => import("./features/AmericanBar"));
const Contact = lazy(() => import("./features/Contact"));
const Events = lazy(() => import("./features/Events"));
const Footer = lazy(() => import("./components/Footer"));
const Machines = lazy(() => import("./features/Machines"));
const Media = lazy(() => import("./features/Media"));
const Tickets = lazy(() => import("./features/Tickets"));

export default function App() {
  useContentProtection();

  // Algorithmic fluid responsiveness calculation in real time
  useEffect(() => {
    const calculateScale = () => {
      const width = window.innerWidth;
      const height = window.innerHeight;

      // Target layout canvas: 1440px wide, 850px high
      const widthScale = width / 1440;
      const heightScale = height / 850;

      // Restrict scale based on the most constrained axis
      let scale = Math.min(widthScale, heightScale);

      // Fluid proportional scaling down to 0.55 for compact or short viewports
      scale = Math.max(0.55, Math.min(1.15, scale));

      document.documentElement.style.setProperty("--r-scale", scale.toFixed(3));
    };

    window.addEventListener("resize", calculateScale);
    // Execute immediately to set initial scale on mount
    calculateScale();

    return () => window.removeEventListener("resize", calculateScale);
  }, []);

  // Basic routing for TV page. O host tv.* (tv.oldschool.plus) e a propria
  // pagina da TV: a raiz dele ja renderiza a TV sem precisar de /tv na URL.
  const [isTVRoute] = useState(() => {
    if (typeof window === "undefined") return false;
    return window.location.pathname === "/tv" || window.location.hostname.startsWith("tv.");
  });

  const [activeSection, setActiveSection] = useState("home");

  // O FUNDO EM CANVAS ENTRA EM 900 ms. Sem gate por gesto.
  //
  // HISTÓRICO — as duas versões anteriores e por que as duas estavam erradas:
  //
  //   1. Sem nenhum controle: o canvas de 1921x860 (1,65 Mpx) repintava desde
  //      o primeiro instante e o TBT era de 23.410 ms. Este é o único fato
  //      desta lista que continua valendo — o canvas REALMENTE não pode
  //      pintar junto com o carregamento.
  //   2. Gate por gesto + teto (8 s, depois 3 s, depois 8 s de novo): tratava
  //      o sintoma escondendo o elemento do medidor. E o preço apareceu: com
  //      tudo adiado, medido em produção sob CPU 4x, o LCP passou a ser o <p>
  //      do hero aos 3.520 ms. Nada disputava a thread — e a página pintava
  //      tarde assim mesmo, porque adiar não acelera nada, só empurra.
  //
  // A LIÇÃO: quanto mais coisa se adia, mais segundos de espera se acumulam.
  // Uma landing page monta de uma vez. Os 900 ms são o bastante para o canvas
  // não competir com a PRIMEIRA pintura do conteúdo (que é o que fixa o FCP e
  // dá o LCP cedo) e curtos o bastante para o visitante não ficar olhando uma
  // cena incompleta.
  const [fundoLiberado, setFundoLiberado] = useState(false);
  useEffect(() => {
    const tid = window.setTimeout(() => setFundoLiberado(true), 900);
    return () => window.clearTimeout(tid);
  }, []);
  const activeSectionRef = useRef(activeSection);

  // Preloader handoff is managed by Hero.tsx (adopts image, then hides overlay)

  // Sync ref when state changes
  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  useEffect(() => {
    const visibleRatios: Record<string, number> = {};

    const observer = new IntersectionObserver(
      (entries) => {
        let maxRatio = 0;
        let currentActive = activeSectionRef.current;

        entries.forEach((entry) => {
          // Update the tracked ratio for this element
          visibleRatios[entry.target.id] = entry.intersectionRatio;
        });

        // Find the section that takes up the most space
        for (const [id, ratio] of Object.entries(visibleRatios)) {
          if (ratio > maxRatio) {
            maxRatio = ratio;
            currentActive = id;
          }
        }

        // Only switch if something is reasonably visible (to avoid empty states between sections)
        if (maxRatio > 0.1 && currentActive !== activeSectionRef.current) {
          setActiveSection(currentActive);
        }
      },
      {
        // Reduced thresholds for performance, still enough for smooth continuous tracking
        threshold: [0, 0.25, 0.5, 0.75],
      },
    );

    const observeSections = () => {
      document
        .querySelectorAll("section, header, footer")
        .forEach((section) => {
          if (section.id) observer.observe(section);
        });
    };

    // Initial binding for synchronously rendered structural tags
    observeSections();

    // React.lazy dynamically injects sections asynchronously *after* mount.
    // We use a MutationObserver to hook them securely into the tracker once they spawn.
    const mutationObserver = new MutationObserver(() => {
      observeSections();
    });
    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [isTVRoute]); // Added dependency to avoid observer setup when not rendering main layout

  if (isTVRoute) {
    return (
      <Suspense fallback={<div className="min-h-svh bg-black" />}>
        <TVPage />
      </Suspense>
    );
  }

  return (
    <div className="relative min-h-svh overflow-x-hidden text-pinball-cream font-sans">
      {/* CRT Scanline Overlay — lazy, logo precisa do próprio boundary: sem
          ele o React lança "A component suspended while responding to
          synchronous input" na primeira atualização síncrona depois do mount.
          O render inicial no navegador disfarçava o defeito. */}
      <Suspense fallback={null}>
        <CRTOverlay />
      </Suspense>

      {/* Navigation */}
      <Navbar activeSection={activeSection} />

      {/* Main Content - transparent so fixed layers (shadow/canvas) show through. z-50 keeps content above all fixed layers */}
      {/* Background Magic — a CENA (foto de ambiente + sombra) entra desde o
          primeiro instante; só a ANIMAÇÃO em canvas espera o gate de gesto.
          Ver a explicação da prop em CanvasBackground. */}
      <Suspense fallback={null}>
        <CanvasBackground
          activeSection={activeSection}
          animacaoLiberada={fundoLiberado}
        />
      </Suspense>

      {/* O Hero fica FORA do boundary das seções lazy de propósito. Um
          Suspense mostra o fallback quando QUALQUER filho suspende — com tudo
          num boundary só, o Hero (import estático, que nunca suspende) era
          apagado junto com as seções abaixo da dobra toda vez que uma delas
          ainda estivesse baixando. */}
      <main
        id="main-content"
        tabIndex={-1}
        className="relative z-50 w-full flex flex-col items-center"
      >
        <Hero />
        <Suspense fallback={null}>
          <Atmosphere />
          <AmericanBar />
          <Machines />
          <Media />
          <Events />
          <Tickets />
        </Suspense>
      </main>

      <Suspense fallback={null}>

        {/* Consolidated Contact & Footer section so activeSection doesn't bounce at the bottom */}
        <section id="contato" className="relative pb-0 z-50">
          <Contact />

          {/* Pre-footer Logo */}
          <div className="flex justify-center py-12 relative z-[100]">
            <img
              src="/images/stream-builders.svg"
              alt="Oldschool.plus"
              className="h-24 md:h-32 w-auto object-contain hover:scale-105 transition-transform duration-300 cursor-pointer"
              width={600}
              height={478}
              loading="lazy"
              decoding="async"
              onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            />
          </div>

          {/* Footer */}
          <Footer />
        </section>

      </Suspense>

      <Suspense fallback={null}>
        <CookieConsent />
      </Suspense>
    </div>
  );
}
